import React, { useMemo } from "react";
import {
  getProductResourceDefaults,
  getRunSectionStep,
  getRunStep,
  getSectionStepInputFields,
  getSectionStepKey,
  getSectionStepUiPatternKey,
  getStepKey,
  getValue
} from "./resourceConfigWizardUtils";
import { SECTION_STEP_REGISTRY, resolveSectionStepControl } from "./stepRegistry";
import { isClientMcpToolsWizard } from "../../workspace/resourceWorkspace.mcpToolsUtils";
import ClientMcpToolsConfiguratorStep from "./steps/ClientMcpToolsConfiguratorStep";

const UnknownStep = ({ context }) => (
  <div className="resource-wizard__pattern">
    <h3>Pattern not implemented</h3>
    <p>The setup metadata references a UI pattern that is not registered in this frontend build.</p>
    <div className="resource-wizard__pattern-facts">
      <div className="resource-wizard__info-pair">
        <span>Pattern key</span>
        <strong>{getSectionStepUiPatternKey(context.sectionStep)}</strong>
      </div>
    </div>
  </div>
);

const normalizeValidationText = (value) => String(value || "").replace(/\s+/g, " ").trim().toLowerCase();

const activeStepValidationIssuesContain = (wizard, step, sectionStep, validationError) => {
  const normalizedError = normalizeValidationText(validationError);
  if (!normalizedError) return false;

  const stepKey = normalizeValidationText(getStepKey(step));
  const sectionStepKey = normalizeValidationText(getSectionStepKey(sectionStep));
  const issues = getValue(wizard, "ValidationIssues", "validationIssues") || [];
  const activeMessages = issues
    .filter((issue) => {
      const issueStepKey = normalizeValidationText(getValue(issue, "StepKey", "stepKey"));
      const issueSectionStepKey = normalizeValidationText(getValue(issue, "SectionStepKey", "sectionStepKey"));
      return (!issueStepKey || issueStepKey === stepKey)
        && (!issueSectionStepKey || issueSectionStepKey === sectionStepKey);
    })
    .map((issue) => normalizeValidationText(getValue(issue, "Message", "message", "DisplayMessage", "displayMessage")))
    .filter(Boolean);

  return activeMessages.length > 0
    && (activeMessages.some((message) => message === normalizedError)
      || activeMessages.every((message) => normalizedError.includes(message)));
};

const WizardStepPanel = ({
  wizard,
  step,
  sectionStep,
  values,
  onChange,
  onSave,
  onContinue,
  onWizardRefresh,
  saving,
  validationError,
  isLastSectionStep,
  registerStepController,
  onOpenSourceObjectManagePanel,
  onDirty,
  readOnly,
  registry = SECTION_STEP_REGISTRY
}) => {
  const productResourceDefaults = useMemo(() => getProductResourceDefaults(wizard), [wizard]);
  const fields = useMemo(
    () => (step && sectionStep ? getSectionStepInputFields(sectionStep, step) : []),
    [sectionStep, step]
  );
  const context = useMemo(() => ({
    wizard,
    step,
    sectionStep,
    values,
    readOnly,
    fields,
    productResourceDefaults,
    saving,
    isLastSectionStep,
    runStep: getRunStep(wizard, getStepKey(step)),
    runSectionStep: getRunSectionStep(wizard, getStepKey(step), getSectionStepKey(sectionStep))
  }), [
    fields,
    isLastSectionStep,
    productResourceDefaults,
    readOnly,
    saving,
    sectionStep,
    step,
    values,
    wizard
  ]);
  const shouldForceClientMcpToolsStep = isClientMcpToolsWizard(wizard)
    && String(getStepKey(step) || "").trim().toLowerCase() === "configure";
  const StepControl = shouldForceClientMcpToolsStep
    ? ClientMcpToolsConfiguratorStep
    : resolveSectionStepControl(sectionStep, registry) || UnknownStep;
  const showValidationError = validationError && !activeStepValidationIssuesContain(wizard, step, sectionStep, validationError);

  if (!step || !sectionStep) {
    return <div className="resource-wizard__state">No setup section step is available.</div>;
  }

  return (
    <section className="resource-wizard__step-panel">
      {showValidationError ? <div className="resource-wizard__inline-error">{validationError}</div> : null}
      <StepControl
        key={`${getStepKey(step)}-${getSectionStepKey(sectionStep)}`}
        context={context}
        onChange={onChange}
        onDirty={onDirty}
        onSave={onSave}
        onContinue={onContinue}
        onWizardRefresh={onWizardRefresh}
        registerStepController={registerStepController}
        onOpenSourceObjectManagePanel={onOpenSourceObjectManagePanel}
      />
    </section>
  );
};

export default WizardStepPanel;
